import React, { useCallback, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import styles from "./PassportRequirementReviewerView.module.css";

import { AddButton, CheckboxItem } from "@cpnw/ui";
import FormDatePicker from "../FormDatePicker";
import PassportDownloadItem from "../PassportDownloadItem";
import DashboardPassportFileEditorItem from "../DashboardPassportFileEditorItem";
import DashboardRadio from "../DashboardRadio";
import PassportMessageForm from "../PassportMessageForm";
import PassportReviewItem from "../PassportReviewItem";

import { Button, InputBox, Loading, SelectBox } from "@cpnw/ui";
import { RequirementContainer } from "hooks/useRequirement";
import { parse, addYears, format, isAfter, add, setDate } from "date-fns";
import QuestionToggle from "components/QuestionToggle";
import classNames from "classnames";
import { formatUTCMidnightToSameDay } from "utils/helpers";
import InsuranceView from "components/RequirementViews/InsuranceView";
import ScreenRespiratorView from "components/RequirementViews/ScreenRespiratorView";
import ImmunizationCovidView from "components/RequirementViews/ImmunizationCovidView";

import ImmunizationTdapView from "components/RequirementViews/ImmunizationTdapView";
import ImmunizationTdapViewNew from "components/RequirementViews/ImmunizationTdapViewNew";

import ImmunizationVaricellaView from "components/RequirementViews/ImmunizationVaricellaView";
import ImmunizationVaricellaViewNew from "components/RequirementViews/ImmunizationVaricellaViewNew";

import ImmunizationMMRView from "components/RequirementViews/ImmunizationMMRView";
import ImmunizationMMRViewNew from "components/RequirementViews/ImmunizationMMRViewNew";

import ImmunizationHepBView from "components/RequirementViews/ImmunizationHepBView";
import ImmunizationHepBViewNew from "components/RequirementViews/ImmunizationHepBViewNew";

import ImmunizationTBView from "components/RequirementViews/ImmunizationTBView";
import { Categories, ContactTypes, DATE_FORMAT_PATTERN, DeclinationEnum } from "pages/enums";
import { SubmissionTypes } from "components/RequirementViews/InsuranceView/InsuranceView";
import { AccountContainer } from "hooks/useAccount";
import CheckrBackgroundCheckView from "components/RequirementViews/CheckrBackgroundCheckView";
import ImmunizationCovidViewNew from "components/RequirementViews/ImmunizationCovidViewNew";
import ImmunizationInfluenzaViewNew from "components/RequirementViews/ImmunizationInfluenzaViewNew";
import CriminalHistoryDisclosureView from "components/RequirementViews/CriminalHistoryDisclosureView";
import { useSubmissionReportCompletedOn } from "hooks/useSubmissionReportCompletedOn";
import { useRequiredImmunizationDocument } from "hooks/useRequiredImmunizationDocument";
import { ImmunizationHepBDeclinationForm } from "components/RequirementViews/ImmunizationHepBViewNew/ImmunizationHepBDeclinationForm";

const states = [
  "AL",
  "AK",
  "AS",
  "AZ",
  "AR",
  "CA",
  "CO",
  "CT",
  "DE",
  "DC",
  "FM",
  "FL",
  "GA",
  "GU",
  "HI",
  "ID",
  "IL",
  "IN",
  "IA",
  "KS",
  "KY",
  "LA",
  "ME",
  "MH",
  "MD",
  "MA",
  "MI",
  "MN",
  "MS",
  "MO",
  "MT",
  "NE",
  "NV",
  "NH",
  "NJ",
  "NM",
  "NY",
  "NC",
  "ND",
  "MP",
  "OH",
  "OK",
  "OR",
  "PW",
  "PA",
  "PR",
  "RI",
  "SC",
  "SD",
  "TN",
  "TX",
  "UT",
  "VT",
  "VI",
  "VA",
  "WA",
  "WV",
  "WI",
  "WY",
];

export default function PassportRequirementReviewerView({
  requirement,
  studentContactID,
  onSaveComplete,
  showRequirementInstructions = true,
  studentInfo,
}) {
  const { userRoles } = AccountContainer.useContainer();

  const isTBTemplate = requirement.subCategory?.Id === 1; // TB
  const isHepBTemplate = requirement.subCategory?.Id === 2; // HepB
  const isMMRMMRVTemplate = requirement.subCategory?.Id === 3; // MMR/MMRV
  const isVaricellaTemplate = requirement.subCategory?.Id === 4; // Varicella
  const isTdapTemplate = requirement.subCategory?.Id === 5; // Tdap
  const isCovid19Template = requirement.subCategory?.Id === 6; // Covid-19
  const isRespiratorTemplate = requirement.subCategory?.Id === 7; // Respirator
  const isHepBNewTemplate = requirement.subCategory?.Id === 8; // HepB(new)
  const isMMRMMRVNewTemplate = requirement.subCategory?.Id === 9; // MMR/MMRV(new)
  const isVaricellaNewTemplate = requirement.subCategory?.Id === 10; // Varicella(new)
  const isTdapNewTemplate = requirement.subCategory?.Id === 11; // Tdap(new)
  const isInfluenzaNewTemplate = requirement.subCategory?.Id === 12; // Influenza
  const isCriminalHistoryDisclosureTemplate = requirement.subCategory?.Id === 14; // Criminal History Disclosure
  const isBLSNewTemplate = requirement.subCategory?.Id === 15; // BLS
  const isCovid19NewTemplate = requirement.subCategory?.Id === 16; // Covid-19(New)

  const rejectionMessageRef = useRef(null);
  const conditionalApprovalMessageRef = useRef(null);
  const insuranceViewRef = useRef();
  const tdapViewRef = useRef();
  const immunizationMMRViewNewRef = useRef();
  const immunizationHepBViewNewRef = useRef();
  const immunizationVaricellaNewRef = useRef();
  const immunizationTdapNewRef = useRef();
  const immunizationCovid19NewRef = useRef();
  const immunizationInfluenzaNewRef = useRef();
  const licenseIndexForFilesRef = useRef(null);
  const licenseFileRef = useRef(null);
  const fileRef = useRef(null);

  // Field DateOfIssue
  const [dateOfIssue, setDateOfIssue] = useState(
    requirement.submission.DateOfIssue
      ? formatUTCMidnightToSameDay(requirement.submission.DateOfIssue, DATE_FORMAT_PATTERN)
      : ""
  );
  const [dateOfIssueChanged, setDateOfIssueChanged] = useState(false);

  // Field DateOfDisclosureSignature
  const [dateOfDisclosureSignature, setDateOfDisclosureSignature] = useState(
    requirement.submission.DateOfDisclosureSignature
      ? formatUTCMidnightToSameDay(requirement.submission.DateOfDisclosureSignature, DATE_FORMAT_PATTERN)
      : ""
  );
  const [dateOfDisclosureSignatureChanged, setDateOfDisclosureSignatureChanged] = useState(false);

  // Field DateOfSignature
  const [dateOfSignature, setDateOfSignature] = useState(
    requirement.submission.DateOfSignature
      ? formatUTCMidnightToSameDay(requirement.submission.DateOfSignature, DATE_FORMAT_PATTERN)
      : ""
  );
  const [dateOfSignatureChanged, setDateOfSignatureChanged] = useState(false);

  // Field RequirementExpirationDate
  const [requirementExpirationDate, setRequirementExpirationDate] = useState("");
  const [requirementExpirationDateChanged, setRequirementExpirationDateChanged] = useState(false);


  // Field Declination
  const [declination, setDeclination] = useState(
    requirement.submission.Declination || null
  );
  const [hepBDeclination, setHepBDeclination] = useState(null);

  const [viewResult, setViewResult] = useState(
    requirement.submission?.JSONContent
      ? JSON.parse(requirement.submission?.JSONContent)
      : {
        reportCompletedOn: null,
        type: "",
        date1: "",
        date2: "",
        manufacturer: "",
        test1: {},
        test2: {},
        serie1: {},
        serie2: {},
      }
  );

  const { visible: visibleSubmissionReportCompletedOn, editable: editableSubmissionReportCompletedOn, approvalOptions } = useSubmissionReportCompletedOn({
    requirementTypeId: requirement?.type?.Id,
    userRoles,
  });

  const requiredImmunizationDocument = useRequiredImmunizationDocument({
    requirementTypeId: requirement?.type?.Id,
  });

  function hasAtLeastOneImmunizationDate(view) {
    const list = Array.isArray(view?.vaccineDates) ? view.vaccineDates : [];
    const vals = list.map(x => typeof x === "string" ? x :
      (x?.immunizationDate || "")
    ).filter(Boolean);
    // return vals.some(s => /^\d{1,2}\/\d{1,2}\/\d{4}$/.test(s.trim()) || /^\d{4}-\d{2}-\d{2}$/.test(s.trim()));
    return vals.some(s => /^\d{1,2}\/\d{1,2}\/\d{4}/.test(s.trim()) || /^\d{4}-\d{2}-\d{2}/.test(s.trim())); // Loose match to allow partial dates
  }

  /**
   * Compute Influenza expiry ("YYYY-09-30") from viewResult + declination.
   * - Declination => null
   * - Accepts dates in MM/DD/YYYY or YYYY-MM-DD
   * - Uses newest immunization date from:
   *     - source.vaccineDates[].immunizationDate
   *     - source.immunizationDate, source.date1, source.date2 (if present)
   */
  // Fixed-season influenza expiry from a view payload.
  // Returns "09/30/YYYY" or null (if declination), or throws if no valid date.

  // ---- Example usage ----
  /*
  const view = {
    vaccineDates: [
      { immunizationDate: "1/15/2025" },
      { immunizationDate: "09/20/2025" }, // newest → expiry 09/30/2026
      "10/18/2027", // even newer → expiry 09/30/2028
    ]
  };*/
  function getInfluenzaExpiration(viewResult, declination) {
    // --- Declination bypass ---
    if (declination) {
      return null;
    }

    // --- Gather candidate dates ---
    const pool = [];
    if (Array.isArray(viewResult && viewResult.vaccineDates)) {
      for (const item of viewResult.vaccineDates) {
        if (!item) continue;
        if (typeof item === "string" || item instanceof Date) {
          pool.push(item);
        } else if (item.immunizationDate) {
          pool.push(item.immunizationDate);
        }
      }
    }

    // --- Parse helpers ---
    // const reIso = /^\d{4}-\d{2}-\d{2}$/;          // 2025-09-20
    // const reUsFixed = /^\d{2}\/\d{2}\/\d{4}$/;    // 09/20/2025
    // const reUsLoose = /^\d{1,2}\/\d{1,2}\/\d{4}$/; // 9/2/2025, 10/18/2027
    const reIso = /^\d{4}-\d{2}-\d{2}/;          // 2025-09-20T...
    const reUsFixed = /^\d{2}\/\d{2}\/\d{4}/;    // 09/20/2025
    const reUsLoose = /^\d{1,2}\/\d{1,2}\/\d{4}/; // 9/2/2025, 10/18/2027

    function toUtcMs(input) {
      if (input instanceof Date) {
        // Normalize to UTC midnight
        return Date.UTC(
          input.getUTCFullYear(),
          input.getUTCMonth(),
          input.getUTCDate()
        );
      }
      if (typeof input !== "string") return NaN;
      const str = input.trim().split("T")[0]; // Remove time portion if present

      if (reIso.test(str)) {
        const [Y, M, D] = str.split("-").map(Number);
        return Date.UTC(Y, M - 1, D);
      }

      if (reUsFixed.test(str) || reUsLoose.test(str)) {
        const [mStr, dStr, yStr] = str.split("/");
        const MM = Number(mStr), DD = Number(dStr), YYYY = Number(yStr);
        if (!Number.isInteger(MM) || !Number.isInteger(DD) || !Number.isInteger(YYYY)) return NaN;
        return Date.UTC(YYYY, MM - 1, DD);
      }

      const t = new Date(str).getTime();
      return Number.isNaN(t) ? NaN : t;
    }

    // --- Pick newest date ---
    let newest = NaN;
    for (const s of pool) {
      const ms = toUtcMs(s);
      if (!Number.isNaN(ms)) newest = Number.isNaN(newest) ? ms : Math.max(newest, ms);
    }
    if (Number.isNaN(newest)) {
      throw new Error("Immunization Date is required");
    }

    // --- Fixed-season expiry ---
    // Season = Aug 1 (Y) → Sep 30 (Y+1)
    // Immunization Aug–Dec → expire Sep 30 of (Y+1)
    // Immunization Jan–Jul → expire Sep 30 of Y
    const d = new Date(newest);
    const y = d.getUTCFullYear();
    const m = d.getUTCMonth() + 1; // 1..12
    const expiryYear = m >= 8 ? y + 1 : y;

    // Always return as MM/DD/YYYY
    return `10/01/${expiryYear}`;
  }

  const getOct1CurrentYear = () => {
    const year = new Date().getFullYear();
    return format(new Date(year, 9, 1), DATE_FORMAT_PATTERN);
  };

  const getCurrentDate = () => {
    return format(new Date(), DATE_FORMAT_PATTERN);
  };

  function calculateBLSExpiration(baseDate) {
    if (!baseDate) return null;

    const parsed = new Date(baseDate);
    if (isNaN(parsed.getTime())) return null;

    // Add 2 years, then set to the last day of that month
    const year = parsed.getFullYear() + 2;
    const month = parsed.getMonth(); // 0 = Jan

    // Create a date for "day 0" of the next month → last day of target month
    const endOfMonth = new Date(year, month + 1, 0);

    // Normalize time to noon to avoid DST edge cases shifting the date
    endOfMonth.setHours(12, 0, 0, 0);

    return format(endOfMonth, DATE_FORMAT_PATTERN);
  }

  function calculateCriminalHistoryDisclosureExpiration(baseDate) {
    if (!baseDate) return null;

    const date = new Date(baseDate);
    if (isNaN(date.getTime())) return null;

    date.setFullYear(date.getFullYear() + 1);

    return format(date, DATE_FORMAT_PATTERN);
  }

  const calculateTdapNewExpiration = ({ fieldADate, fieldBDate, isDeclined, saveDate }) => {
    const parseDate = (d) => d ? new Date(d) : null;

    const dateA = parseDate(fieldADate);
    const dateB = parseDate(fieldBDate);
    const saveDt = new Date(saveDate);

    // If declined, return 1 year from saveDate
    if (isDeclined) {
      const oneYearFromSave = new Date(saveDt);
      oneYearFromSave.setFullYear(saveDt.getFullYear() + 1);
      return formatUTCMidnightToSameDay(oneYearFromSave.toISOString().split('T')[0]);
    }

    // Neither A nor B entered
    if (!dateA && !dateB) {
      throw new Error("You must enter a date in either Field A or Field B, or select Declination.");
    }

    // Use the newest of A and B
    const latestDate = !dateA ? dateB
      : !dateB ? dateA
        : (dateA > dateB ? dateA : dateB);

    const expirationDate = new Date(latestDate);
    expirationDate.setFullYear(expirationDate.getFullYear() + 10);

    return format(expirationDate, DATE_FORMAT_PATTERN);
  };

  function calculateInfluenzaExpiration(baseDate) {
    if (!baseDate) return null;

    const date = new Date(baseDate);
    if (isNaN(date.getTime())) return null;

    date.setFullYear(date.getFullYear() + 1);

    return format(date, DATE_FORMAT_PATTERN);
  }

  // Auto-calc Influenza expiration per fixed-season rule, using NEWEST date.
  // Declination → no date required & no expiry set.
  useEffect(() => {
    if (!isInfluenzaNewTemplate) return;

    try {
      const exp = getInfluenzaExpiration(viewResult, declination);
      // exp is either "YYYY-09-30" or null (declined)
      setRequirementExpirationDate(exp || null);
      setRequirementExpirationDateChanged(true);
      console.log("Influenza expiry is:", exp);
    } catch (err) {
      // Missing/invalid dates when not declined → leave empty so validator can warn.
      console.warn("Influenza expiry not set:", err.message);
      setRequirementExpirationDate(null);
    }
  }, [isInfluenzaNewTemplate, viewResult, declination]);

  useEffect(() => {
    if (requirementExpirationDateChanged) return;

    const safeFormat = (date) =>
      date ? formatUTCMidnightToSameDay(date, DATE_FORMAT_PATTERN) : "";

    let newExpiration = "";

    if (isInfluenzaNewTemplate) {
      newExpiration = requirement.expiresOn
        ? safeFormat(requirement.expiresOn)
        : requirement.annualDate || getOct1CurrentYear();
    } else if (isCriminalHistoryDisclosureTemplate) {
      newExpiration = requirement.expiresOn
        ? safeFormat(requirement.expiresOn)
        : requirement.annualDate || calculateCriminalHistoryDisclosureExpiration(getCurrentDate());
    } else if (isBLSNewTemplate) {
      newExpiration = requirement.expiresOn
        ? safeFormat(requirement.expiresOn)
        : requirement.annualDate || calculateBLSExpiration(getCurrentDate());
    } else {
      newExpiration = safeFormat(requirement.expiresOn);
    }

    setRequirementExpirationDate(newExpiration);
  }, [
    requirement,
    requirement.expiresOn,
    requirement.annualDate,
    isInfluenzaNewTemplate,
    isCriminalHistoryDisclosureTemplate,
    isBLSNewTemplate,
    requirementExpirationDateChanged,
  ]);


  useEffect(() => {
    if (!isBLSNewTemplate) return;
    if (!dateOfIssue) return;

    try {
      const exp = calculateBLSExpiration(dateOfIssue);
      setRequirementExpirationDate(exp);
      setRequirementExpirationDateChanged(true);
    } catch (err) {
      console.warn("Expiration not set:", err.message);
      setRequirementExpirationDate(null);
    }
  }, [dateOfIssue, isBLSNewTemplate]);

  useEffect(() => {
    const disclosureSignatureDates = viewResult.disclosureSignatureDates || [];
    if (!isCriminalHistoryDisclosureTemplate) return;
    if (disclosureSignatureDates.length == 0) return;

    try {
      const latestDate = disclosureSignatureDates.reduce((latest, current) => {
        const currentDate = new Date(current.dateOfDisclosureSignature);
        return isAfter(currentDate, latest) ? currentDate : latest;
      }, new Date(0)); // Start with the earliest date

      const exp = calculateCriminalHistoryDisclosureExpiration(latestDate);
      setRequirementExpirationDate(exp);
      setRequirementExpirationDateChanged(true);
    } catch (err) {
      console.warn("Expiration not set:", err.message);
      setRequirementExpirationDate(null);
    }
  }, [viewResult, isCriminalHistoryDisclosureTemplate]);

  useEffect(() => {
    if (!isInfluenzaNewTemplate) return;
    if (!dateOfSignature) return;

    try {
      const exp = calculateInfluenzaExpiration(dateOfSignature);
      setRequirementExpirationDate(exp);
      setRequirementExpirationDateChanged(true);
    } catch (err) {
      console.warn("Expiration not set:", err.message);
      setRequirementExpirationDate(null);
    }
  }, [dateOfSignature, isInfluenzaNewTemplate]);

  useEffect(() => {
    if (isTdapNewTemplate) {
      const content = JSON.parse(requirement.submission?.JSONContent || "{}");

      if (!content.date1 && !content.date2 && !declination) return;
      setRequirementExpirationDateChanged(true);
      try {
        const exp = calculateTdapNewExpiration({
          fieldADate: content.date1,
          fieldBDate: content.date2,
          isDeclined: declination,
          saveDate: format(new Date(), DATE_FORMAT_PATTERN),
        });

        setRequirementExpirationDate(exp);
      } catch (err) {
        console.warn("Expiration not set:", err.message);
        setRequirementExpirationDate(null); // optional fallback
      }
    }
  }, [requirement.submission?.JSONContent, declination, isTdapNewTemplate]);

  const {
    useRequirementMessages,
    postRequirementMessage,
    postApproval,
    submitRequirement,
    // sendHealthReasonNotification
  } = RequirementContainer.useContainer();

  const { data: messagesData, refetch: refetchMessages } =
    useRequirementMessages(requirement.contactRequirementId);
  const messages = (messagesData || requirement.messages || []).map((a) => {
    const allMessages = messagesData || requirement.messages || [];

    const repliedToMessage =
      a.RepliedToMessageId != null
        ? allMessages.find((m) => m.Id === a.RepliedToMessageId)
        : null;

    return {
      messageId: a.Id,
      fromContactId: a.FromContactId,
      currentContactId: a.CurrentContactId,
      name: a.FromContact,
      date: format(a.RepliedOn, "Pp"),
      message: a.Message,
      repliedToMessage: repliedToMessage
        ? {
          name: repliedToMessage.FromContact,
          date: format(repliedToMessage.RepliedOn, "Pp"),
          message: repliedToMessage.Message,
        }
        : null,
    };
  });

  const canReview = requirement.canReview;
  const canView = requirement.canView;
  const canEdit = requirement.canEdit;

  //HepB, MMR, Varicella, Tdap, Covid-19 do not require expiration date
  const expirationDateRequired = requirement.frequency !== "Once" && (!requirement.subCategory || ![2, 3, 4, 5, 6, 8, 9, 10, 11, 16].includes(requirement.subCategory.Id));

  const roles = userRoles.map((a) => a.toUpperCase());
  const canDeleteFile = () => {
    if (
      roles.includes("CPNW-REVIEWER") ||
      roles.includes("EDUCATION") || roles.includes("FACULTY") ||
      roles.includes("HEALTHCARE")
    ) {
      return true;
    }
    return false;
  };

  const [errors, setErrors] = useState([]);

  const handleDeclinationChange = (value) => {
    setDeclination(value);

    // Only wipe HepB declination form if value is outside the 3 form options
    if (
      isHepBNewTemplate &&
      ![
        DeclinationEnum.NonConverter,
        DeclinationEnum.SeriesInProcess,
        DeclinationEnum.DeclinationForm
      ].includes(value)
    ) {
      setViewResult(prev => ({
        ...prev,
        reviewer: {
          declination: "",
          files: [],
          newFiles: [],
          newFileNames: []
        }
      }));
    }
  };
  const handleClearDeclination = () => {
    setDeclination(null);

    if (isHepBNewTemplate) {
      // Clear reviewer declination and files
      setViewResult(prev => ({
        ...prev,
        reviewer: {
          declination: "",
          files: [],
          newFiles: [],
          newFileNames: []
        }
      }));
    }

  };

  const handleOnSend = useCallback(
    (value, repliedToMessage, groupOfRecipients) => {
      console.log("requirement", requirement);
      postRequirementMessage({
        contactRequirementId: requirement.contactRequirementId,
        message: value,
        repliedToMessageId: repliedToMessage ? repliedToMessage.messageId : null,
        groupOfRecipients: groupOfRecipients
      }).then(() => {
        refetchMessages();
      });
    },
    [requirement]
  );

  const [immunizationDate, setImmunizationDate] = useState(
    requirement.submission.ImmunizationDate
      ? formatUTCMidnightToSameDay(
        requirement.submission.ImmunizationDate,
        DATE_FORMAT_PATTERN
      )
      : null
  );
  const [expirationDate, setExpirationDate] = useState(
    requirement.submission.ExpiresOn
      ? formatUTCMidnightToSameDay(
        requirement.submission.ExpiresOn,
        DATE_FORMAT_PATTERN
      )
      : null
  );



  const [isWaived, setIsWaived] = useState(requirement.isWaived);

  const [rejectionMessage, setRejectionMessage] = useState("");
  const [conditionalApprovalMessage, setConditionalApprovalMessage] = useState(
    requirement.approvalConditions
  );


  const [location, setLocation] = useState(
    requirement.submission.Location || ""
  );
  const [files, setFiles] = useState(requirement.submission.files || []);
  const [newFiles, setNewFiles] = useState([]);

  const [hasLicenses, setHasLicenses] = useState(
    requirement.hasLicenses ? "yes" : "no"
  );
  const [licenses, setLicenses] = useState(
    requirement.hasLicenses
      ? (requirement.submissions || []).map((license) => {
        return {
          id: license.Id,
          licenseType: license.LicenseType,
          licenseNumber: license.LicenseNumber,
          issuedBy: license.IssuedBy,
          issuedOn: license.IssuedOn
            ? formatUTCMidnightToSameDay(license.IssuedOn)
            : "",
          expiresOn: license.ExpiresOn
            ? formatUTCMidnightToSameDay(license.ExpiresOn)
            : "",
          files: (license.Files || []).map((a) => ({
            id: a.Id,
            name: a.Name,
            url: a.Url,
          })),
          newFiles: [],
        };
      })
      : []
  );

  const handleChange = useCallback(
    (license, value, update) => {
      let name = update;

      const li = licenses.find((a) => a.id == license.id);
      li[name] = value;
      setLicenses([...licenses]);
    },
    [licenses]
  );

  const onAddMoreLicense = useCallback(() => {
    licenses.push({
      id: Math.random(),
      newFiles: [],
    });
    setLicenses([...licenses]);
  }, [licenses]);



  const onUploadLicenses = (index) => {
    licenseIndexForFilesRef.current = index;
    licenseFileRef.current.click();
  };

  const onLicenseFileChange = (e) => {
    const index = licenseIndexForFilesRef.current;
    const license = licenses[index];
    license.newFiles = e.target.files;
    setLicenses([...licenses]);
  };

  const onFileChange = (e) => {
    const addFiles = e.target.files;
    for (const f of addFiles) {
      if (f.name.length >= 255) {
        alert("Please use file name with less than 255 characters.");
        return;
      }
      f.id = Math.random();
    }
    setNewFiles((newFiles) => [...newFiles, ...addFiles]);
  };

  function removeFileFromFileList(fileList, indexToRemove) {
    const dt = new DataTransfer();

    for (let i = 0; i < fileList.length; i++) {
      if (i !== indexToRemove) {
        dt.items.add(fileList[i]);
      }
    }

    return dt.files;
  }



  const onSetHasLicenses = useCallback(
    (value) => {
      if (value == "yes" && licenses.length == 0) {
        licenses.push({
          id: Math.random(),
          newFiles: [],
        });
        setLicenses([...licenses]);
      }

      setHasLicenses(value);
    },
    [hasLicenses, licenses]
  );

  const [approval, setApproval] = useState(requirement.status);

  useEffect(() => {
    setErrors([]);
  }, [approval]);

  const handleApprovalChange = (value) => {
    setApproval(value);
  };

  const [isLoading, setIsLoading] = useState(false);

  // const onSaveStudent = async () => {
  //   setIsLoading(true);
  //   try {
  //     const doesHaveLicenses = hasLicenses == "yes";
  //     const dto = {
  //       studentContactID,
  //       contactRequirementId: requirement.contactRequirementId,
  //       submissionId: requirement.submission.Id,
  //       licenses: doesHaveLicenses
  //         ? licenses.map((license) => {
  //           return {
  //             ...license,
  //             newFiles: Array.from(license.newFiles).map((f) => f.name),
  //           };
  //         })
  //         : [],
  //       declination: declination,
  //       immunizationDate,
  //       expirationDate,
  //       location,
  //       hasLicenses: doesHaveLicenses,
  //       files,
  //       newFiles: newFiles.map((a) => a.name),
  //       viewResult: JSON.stringify(viewResult),
  //       isWaived,
  //     };

  //     await submitRequirement(
  //       dto,
  //       [...newFiles].concat(
  //         licenses.flatMap((license) => {
  //           return Array.from(license.newFiles);
  //         })
  //       )
  //     ).then(() => {
  //       if (onSaveComplete) {
  //         onSaveComplete();
  //       }
  //     });

  //     console.log("UserInputView requirement", requirement);
  //     console.log("UserInputView OnSave dto", dto);
  //   } catch (error) {
  //     // Handle any errors here
  //     console.error("onSave failed:", error);
  //   } finally {
  //     // Set loading state to false, regardless of success or failure
  //     setIsLoading(false);
  //   }
  // };
  console.log('requirement', requirement)


  const [validationErrors, setValidationErrors] = useState({});
  const handleValidation = (errors) => {
    setValidationErrors((prevErrors) => ({ ...prevErrors, ...errors }));
  };

  const validator = () => {
    if (requirement.byPassStudentInfoValidation) {
      return true;
    }

    let newErrors = [];
    const noFiles =
      (files?.length || 0) + (newFiles?.length || 0) === 0;

    const isBlank = (v) =>
      v == null ||
      (typeof v === "string" && v.trim() === "");

    if (Categories.CERTS === requirement.type.Id) {
      if (isBLSNewTemplate) {
        const invalidIssueDate =
          isBlank(dateOfIssue) ||
          (dateOfIssue instanceof Date && isNaN(dateOfIssue.getTime()));

        if (invalidIssueDate && noFiles) {
          newErrors.push("Please provide a Date of Issue or upload a document.");
        }
      } else {
        const invalidExpDate =
          isBlank(expirationDate) ||
          (expirationDate instanceof Date && isNaN(expirationDate.getTime()));

        if (invalidExpDate && noFiles) {
          newErrors.push("Please provide an Expiration Date or upload a document.");
        }
      }
    } else if (Categories.LICENSES == requirement.type.Id) {
    } else if (Categories.ELEARNING == requirement.type.Id) {
    } else if (Categories.FORMS == requirement.type.Id) {
      if (files.concat(newFiles).length === 0) {
        newErrors.push("Please upload a document.");
      }
    } else if (Categories.IMMUNIZATION == requirement.type.Id) {
      if (!requirement.subCategory) {
        if (!declination && files.concat(newFiles).length === 0) {
          newErrors.push(
            "Please select a declination option or upload a document."
          );
        }
      } else if (
        requirement.subCategory &&
        requirement.subCategory.Name == "Tdap"
      ) {
        const tdapViewErrors = tdapViewRef.current
          ? tdapViewRef.current.validate()
          : null;

        if (tdapViewErrors && Object.keys(tdapViewErrors).length > 0) {
          setValidationErrors(tdapViewErrors);
          newErrors.push("");
        }
      } else if (isTdapNewTemplate) {
        const tdapViewNewErrors = immunizationTdapNewRef.current
          ? immunizationTdapNewRef.current.validate()
          : null;

        if (tdapViewNewErrors && Object.keys(tdapViewNewErrors).length > 0) {
          setValidationErrors(tdapViewNewErrors);
          newErrors.push("");
        }
      } else if (isMMRMMRVNewTemplate) {
        immunizationMMRViewNewRef.current.validator();
        if (!viewResult.type && !declination) {
          newErrors.push(
            "Please select a submission option or a declination option."
          );
        }
      } else if (isHepBNewTemplate) {
        const errors = immunizationHepBViewNewRef.current.validator();

        // 1️⃣ Check submission option
        if (!viewResult.type) {
          newErrors.push("Please select a submission option.");
        }

        // 2️⃣ Check declination option
        // if (!declination) {
        //   newErrors.push("Please select a declination option.");
        // }

        // 3️⃣ Collect field-level errors from the form
        if (errors) {
          Object.values(errors).forEach((msg) => {
            if (msg) newErrors.push(msg);
          });
        }
      } else if (isInfluenzaNewTemplate) {
        if ((!viewResult.vaccineDates || viewResult.vaccineDates.length === 0 || !hasAtLeastOneImmunizationDate(viewResult)) && !declination) {
          newErrors.push("Please enter vaccinate date(s)."
          );
        } else if (declination) {// If declined, skip date checks entirely
          // OK: no date required, no expiry
        } else {
          // Not declined → require an immunization date (any of the supported fields)
          const exp = (() => {
            try {
              const newExp = getInfluenzaExpiration(viewResult, declination);
              setRequirementExpirationDate(newExp || null);
              setRequirementExpirationDateChanged(true);
              console.log("Influenza expiry is:", newExp);
              return newExp
            }
            catch { return null; }
          })();
          if (!exp) newErrors.push("Please enter vaccinate date(s).");
        }
      } else if (isVaricellaNewTemplate) {
        immunizationVaricellaNewRef.current.validator();
        if (!viewResult.type && !viewResult.manufacturer && !viewResult.date1 && !viewResult.date2 && !declination) {
          newErrors.push("Please select a submission option or a declination option.");
        }
      } else if (isCovid19NewTemplate) {
        immunizationCovid19NewRef.current.validator();
      } else {
        if (!viewResult.type && !declination) {
          newErrors.push(
            "Please select a submission option or a declination option."
          );
        }
      }
    } else if (Categories.INSURANCE == requirement.type.Id) {
      const insuranceViewErrors = insuranceViewRef.current
        ? insuranceViewRef.current.validate()
        : null;

      if (insuranceViewErrors && Object.keys(insuranceViewErrors).length > 0) {
        setValidationErrors(insuranceViewErrors);
        newErrors.push("");
      } else if (
        viewResult.type == SubmissionTypes.B &&
        files.concat(newFiles).length === 0
      ) {
        newErrors.push("Please upload a document.");
      }
    } else if (Categories.SCREENS_CHECKS == requirement.type.Id) {
      if (!declination && files.concat(newFiles).length === 0) {
        newErrors.push(
          "Please select a declination option or upload a document."
        );
      }
    } else if (Categories.SITE_ORIENTATIONS == requirement.type.Id) {
    }

    setErrors(newErrors);
    // If all checks pass, allow continuous
    return newErrors.length > 0 ? false : true;
  };

  const onSave = async () => {
    if (!canEdit) {
      setErrors(["You cannot edit this requirement"]);
      return;
    }

    if (!roles.includes("CPNW-REVIEWER")) {
      if (!validator()) {
        return;
      }
    }

    let exp = null;
    if (isTdapNewTemplate) {
      exp = calculateTdapNewExpiration({
          fieldADate: viewResult.date1,
          fieldBDate: viewResult.date2,
          isDeclined: declination,
          saveDate: format(new Date(), DATE_FORMAT_PATTERN),
        });
    } else if (isInfluenzaNewTemplate) {
      exp = getInfluenzaExpiration(viewResult, declination);
    } else if (isCriminalHistoryDisclosureTemplate) {
      const disclosureSignatureDates = viewResult.disclosureSignatureDates || [];
      if (disclosureSignatureDates.length != 0) {
        const latestDate = viewResult.disclosureSignatureDates.reduce((latest, current) => {
          const currentDate = new Date(current.dateOfDisclosureSignature);
          return isAfter(currentDate, latest) ? currentDate : latest;
        }, new Date(0));
        exp = calculateCriminalHistoryDisclosureExpiration(latestDate);
      }
    } else if (isBLSNewTemplate) {
      exp = calculateBLSExpiration(dateOfIssue);
    }

    const doesHaveLicenses = hasLicenses == "yes";

    const dto = {
      contactId: requirement.contactId,
      contactRequirementId: requirement.contactRequirementId,
      submissionId: requirement.submission.Id,
      licenses: doesHaveLicenses
        ? licenses.map((license) => {
          return {
            ...license,
            newFiles: Array.from(license.newFiles).map((f) => f.name),
          };
        })
        : [],
      declination: declination,
      immunizationDate,
      expirationDate: !exp ? expirationDate : exp,
      location,
      hasLicenses: doesHaveLicenses,
      files,
      newFiles: newFiles.map((a) => a.name),
      approval,
      rejectionMessage,
      conditionalApprovalMessage,
      viewResult: JSON.stringify(viewResult),
      isWaived,
      // ...(isHepBNewTemplate && {
      // hepBNewFiles: hepBNewFiles.map(f => f.name),
      // declinationNewFiles: hepBDeclination.newFiles.map(f => f.name),
      // }),
      ...(isBLSNewTemplate && {
        dateOfIssue,
        requirementExpirationDate
      }),
      ...(isInfluenzaNewTemplate && {
        dateOfSignature,
        requirementExpirationDate: exp
      }),
      ...(isCriminalHistoryDisclosureTemplate && {
        dateOfDisclosureSignature,
        requirementExpirationDate
      }),
      ...(isTdapNewTemplate && { requirementExpirationDate: exp }),
    };

    if (canReview) {
      const skipExpirationAndMessageCheck = isBLSNewTemplate || isInfluenzaNewTemplate || isTdapNewTemplate || isCriminalHistoryDisclosureTemplate;

      if (approval === "Approved" || approval === "Conditionally Approved") {
        if (!skipExpirationAndMessageCheck && expirationDateRequired && !requirementExpirationDate) {
          setErrors(["Must enter Requirement Expiration Date"]);
          return;
        }

        if (
          approval === "Conditionally Approved" &&
          !skipExpirationAndMessageCheck &&
          !conditionalApprovalMessage
        ) {
          conditionalApprovalMessageRef.current.focus();
          setErrors(["Must enter Conditions Message when selecting Conditionally Approved"]);
          return;
        }

        // Only assign if required and not skipping
        if (!skipExpirationAndMessageCheck && expirationDateRequired) {
          dto.requirementExpirationDate = requirementExpirationDate;
        }
      } else if (approval === "Rejected") {
        if (!rejectionMessage) {
          rejectionMessageRef.current.focus();
          setErrors(["Must enter Rejection Message when selecting Reject"]);
          return;
        }
      } else {
        if (!roles.includes("CPNW-REVIEWER")) {
          setErrors(["Please select either Accept or Reject"]);
          return;
        }
      }
    }

    setErrors([]);
    setIsLoading(true);

    try {
      await postApproval(
        dto,
        [...newFiles,
        ...(isHepBNewTemplate ? Object.values(viewResult)
          .flatMap(section => Array.from(section?.newFiles || []).filter(f => f instanceof File)) : []),
          // ...(isHepBNewTemplate ? hepBDeclination.newFiles : []),
        ].concat(
          licenses.flatMap((license) => {
            return Array.from(license.newFiles);
          })
        )
      );

      if (isHepBNewTemplate) {
        immunizationHepBViewNewRef.current.onSaveComplete();
      }
      onSaveComplete();
    } catch (e) {
      console.error("postApproval failed", e);
      try {
        const body = JSON.parse(e.message);
        setErrors(body.errors?.length > 0 ? body.errors : ["An error occurred while saving. Please try again."]);
      } catch {
        setErrors([e.message || "An error occurred while saving. Please try again."]);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const renderDocuments = () => {
    return requirement.type.HasDocumentation == true ? (
      <>
        <div className={styles.uploadRow}>
          <p className={styles.paragraph}>Upload Documentation</p>
          {canEdit && (
            <AddButton
              label="Add File"
              onClick={() => {
                fileRef.current.click();
              }}
            />
          )}
          <input
            type="file"
            multiple={true}
            onChange={(e) => onFileChange(e)}
            ref={fileRef}
            style={{ display: "none" }}
          />
        </div>
        <div className={styles.fileSetup}>
          {files.map((f, idx) => (
            <DashboardPassportFileEditorItem
              key={idx}
              name={f.name}
              onView={() => {
                console.log("onView ", f);
                window.open(f.url, "_blank");
              }}
              onDelete={() => {
                console.log("onDelete ", f);
                if (canDeleteFile()) {
                  const confirmation = window.confirm(
                    "Please ensure the approval status aligns with document deletion. Are you sure you want to delete this document?"
                  );
                  if (confirmation) {
                    setFiles((files) =>
                      files.filter((file) => file.id !== f.id)
                    );
                  }
                }
              }}
              canDelete={canEdit && canDeleteFile()}
            />
          ))}
          {Array.from(newFiles).map((f, idx) => (
            <DashboardPassportFileEditorItem
              key={idx}
              name={f.name}
              onView={() => {
                console.log("onView ", f);
                window.open(URL.createObjectURL(f), "_blank");
              }}
              onDelete={() => {
                console.log("onDelete ", f);
                if (canDeleteFile()) {
                  const confirmation = window.confirm(
                    "Are you sure you want to delete this document?"
                  );
                  if (confirmation) {
                    setNewFiles((files) =>
                      files.filter((file) => file.id != f.id)
                    );
                  }
                }
              }}
              canDelete={canEdit}
            />
          ))}
        </div>
      </>
    ) : null;
  };

  const handleHealthReasonNotify = async () => {
    // try {
    //   await sendHealthReasonNotification({
    //     contactRequirementId: requirement.contactRequirementId
    //   });

    //   console.log("Health Reason notifications sent successfully");
    // } catch (err) {
    //   console.error("Failed to send Health Reason notifications:", err);
    // }
  };

  return (
    <>
      <div className={classNames(styles.header, styles.stickyHeader)}>
        <h3>
          {studentInfo.lastName}, {studentInfo.firstName}
        </h3>
      </div>
      <div className={classNames(styles.container, styles.customContainer)}>
        <div className={classNames(styles.column, styles.column1)}>
          <div className={styles.row}>
            <PassportReviewItem
              label="Requirement Name"
              description={requirement.requirementName}
            />
            <PassportReviewItem
              label="Abbr"
              description={requirement.abbreviation}
            />
            <PassportReviewItem
              label="Required By"
              description={
                Array.isArray(requirement.requiredBy)
                  ? requirement.requiredBy[0]
                  : requirement.requiredBy
              }
            />
            <PassportReviewItem
              label="Frequency"
              description={requirement.frequency}
            />
          </div>
          {showRequirementInstructions && (
            <div>
              <div className={styles.instructions}>
                <PassportReviewItem
                  label="Instructions"
                  description={requirement.instructions}
                />
              </div>
              <div className={styles.row}>
                <div className={styles.column}>
                  <p className={styles.paragraph}>Files/Forms</p>
                  <div className={styles.fileSetup}>
                    {requirement.requirementFiles.map((f) => {
                      return <PassportDownloadItem file={f} />;
                    })}
                  </div>
                </div>
              </div>
            </div>
          )}

          <hr>{/* Horizontal Rule */}</hr>
          <h4>Submission:</h4>
          {requirement.allowWaiver && (
            <div className={styles.column}>
              <CheckboxItem
                className={styles.checkbox}
                onClick={(isChecked) => setIsWaived(!!isChecked)}
                description={"Waived"}
                isChecked={isWaived}
                disabled={!canEdit}
              />
            </div>
          )}
          {!isWaived && (
            <div>
              {visibleSubmissionReportCompletedOn && (
                <CheckrBackgroundCheckView
                  data={viewResult}
                  onChange={(result) => {
                    setViewResult(result);
                    setErrors([]);
                  }}
                  // disabled={!editableSubmissionReportCompletedOn} // Don't disable input as of Jan 24, 2026 per CPNW request
                  onValidate={handleValidation}
                />
              )}
              {/* {Categories.IMMUNIZATION == requirement.type.Id && // Old Influenza Template
                !requirement.subCategory && (
                  <div
                    className={classNames(
                      styles.row,
                      styles.columnImmunizationDate
                    )}
                  >
                    <FormDatePicker
                      label="Immunization Date"
                      dateFormat={DATE_FORMAT_PATTERN}
                      dateString={immunizationDate}
                      setDate={setImmunizationDate}
                      disabled={!canEdit}
                    />
                    <div className={styles.column}>
                      <InputBox
                        label="Location Received"
                        placeholder="Provider Name & Location"
                        value={location}
                        setValue={(e) => {
                          setLocation(e.target.value);
                        }}
                        disabled={!canEdit}
                      />
                    </div>
                  </div>
                )} */}
              {/* {isInfluenzaTemplate && (
                <div
                  className={classNames(
                    styles.row,
                    styles.columnImmunizationDate
                  )}
                >
                  <FormDatePicker
                    label="Immunization Date"
                    dateFormat={DATE_FORMAT_PATTERN}
                    dateString={immunizationDate}
                    setDate={setImmunizationDate}
                    disabled={!canEdit}
                  />
                  <div className={styles.column}>
                    <InputBox
                      label="Location Received"
                      placeholder="Provider Name & Location"
                      value={location}
                      setValue={(e) => {
                        setLocation(e.target.value);
                      }}
                      disabled={!canEdit}
                    />
                  </div>
                </div>
              )} */}

              {isBLSNewTemplate && (
                <div className={classNames(styles.row)}>
                  <FormDatePicker
                    label="Date of Issue"
                    dateFormat={DATE_FORMAT_PATTERN}
                    dateString={dateOfIssue}
                    setDate={(value) => {
                      setDateOfIssue(value);
                      setDateOfIssueChanged(true);
                    }}
                    disabled={!canEdit}
                  />
                </div>
              )}
              {requirement.subCategory &&
                requirement.subCategory.Name == "TB" && (
                  <ImmunizationTBView
                    data={viewResult}
                    disabled={!canEdit}
                    onChange={(result) => {
                      console.log("ImmunizationTBView result", result);
                      setViewResult(result);
                    }}
                  />
                )}
              {requirement.subCategory &&
                requirement.subCategory.Name == "HepB" && (
                  <ImmunizationHepBView
                    data={viewResult}
                    disabled={!canEdit}
                    onChange={(result) => {
                      console.log("ImmunizationHepBView result", result);
                      setViewResult(result);
                    }}
                  />
                )}
              {isHepBNewTemplate && (
                <ImmunizationHepBViewNew
                  data={viewResult}
                  disabled={!canEdit}
                  roles={roles}
                  onNotify={handleHealthReasonNotify}
                  onChange={(result) => {
                    console.log("ImmunizationHepBViewNew result", result);
                    setViewResult(result);
                  }}
                  ref={immunizationHepBViewNewRef}
                />
              )}
              {requirement.subCategory &&
                requirement.subCategory.Name == "MMR/MMRV" && (
                  <ImmunizationMMRView
                    data={viewResult}
                    disabled={!canEdit}
                    onChange={(result) => {
                      console.log("ImmunizationMMRView result", result);
                      setViewResult(result);
                    }}
                  />
                )}
              {requirement.subCategory &&
                requirement.subCategory.Name == "MMR/MMRV(new)" && (
                  <ImmunizationMMRViewNew
                    data={viewResult}
                    disabled={!canEdit}
                    onChange={(result) => {
                      console.log("ImmunizationMMRView result", result);
                      setViewResult(result);
                    }}
                    ref={immunizationMMRViewNewRef}
                  />
                )}
              {requirement.subCategory &&
                requirement.subCategory.Name == "Varicella" && (
                  <ImmunizationVaricellaView
                    data={viewResult}
                    disabled={!canEdit}
                    onChange={(result) => {
                      console.log("ImmunizationVaricellaView result", result);
                      setViewResult(result);
                    }}
                  />
                )}
              {isVaricellaNewTemplate && (
                <ImmunizationVaricellaViewNew
                  data={viewResult}
                  disabled={!canEdit}
                  onChange={(result) => {
                    console.log("ImmunizationVaricellaView result", result);
                    setViewResult(result);
                  }}
                  ref={immunizationVaricellaNewRef}
                />
              )}
              {requirement.subCategory &&
                requirement.subCategory.Name == "Tdap" && (
                  <ImmunizationTdapView
                    data={viewResult}
                    selectedDeclination={declination}
                    disabled={!canEdit}
                    onChange={(result) => {
                      console.log("ImmunizationTdapView result", result);
                      setViewResult(result);
                    }}
                    ref={tdapViewRef}
                    onValidate={handleValidation}
                  />
                )}
              {isTdapNewTemplate && (
                <ImmunizationTdapViewNew
                  data={viewResult}
                  selectedDeclination={declination}
                  disabled={!canEdit}
                  onChange={(result) => {
                    console.log("ImmunizationTdapView result", result);
                    setViewResult(result);
                  }}
                  ref={immunizationTdapNewRef}
                  setDate={setImmunizationDate}
                  onValidate={handleValidation}
                />
              )}
              {isCriminalHistoryDisclosureTemplate && (
                <CriminalHistoryDisclosureView
                  data={viewResult}
                  onChange={(result) => {
                    console.log("CriminalHistoryDisclosureView result", result);
                    setViewResult(result);
                    setErrors([]);
                  }}
                  disabled={!canEdit}
                  onValidate={handleValidation}
                />
              )}
              {requirement.subCategory &&
                requirement.subCategory.Name == "Covid-19" && (
                  <ImmunizationCovidView
                    data={viewResult}
                    disabled={!canEdit}
                    onChange={(result) => {
                      console.log("ImmunizationCovidView result", result);
                      setViewResult(result);
                    }}
                  />
                )}
              {isCovid19NewTemplate && (
                <ImmunizationCovidViewNew
                  data={viewResult}
                  onChange={(result) => {
                    console.log("ImmunizationCovidView result", result);
                    setViewResult(result);
                  }}
                  disabled={!canEdit}
                  ref={immunizationCovid19NewRef}
                />
              )}
              {requirement.subCategory &&
                requirement.subCategory.Name == "Respirator" && (
                  <ScreenRespiratorView
                    data={viewResult}
                    disabled={!canEdit}
                    onChange={(result) => {
                      console.log("ImmunizationRespiratorView result", result);
                      setViewResult(result);
                    }}
                  />
                )}
              {requirement.type.Name == "Insurance" && (
                <InsuranceView
                  data={viewResult}
                  disabled={!canEdit}
                  onChange={(result) => {
                    console.log("InsuranceView result", result);
                    setViewResult(result);
                    setErrors([]);
                  }}
                  ref={insuranceViewRef}
                  onValidate={handleValidation}
                />
              )}
              {(requirement.type.HasImmunizationDate ||
                requirement.type.HasLocation ||
                requirement.type.HasExpiration) && (
                  <div className={styles.row}>
                    {(requirement.type.HasImmunizationDate) && (!requirement.subCategory || requirement.subCategory.HasImmunizationDate) && (
                      <FormDatePicker
                        label="Immunization Date"
                        dateFormat={DATE_FORMAT_PATTERN}
                        dateString={immunizationDate}
                        setDate={setImmunizationDate}
                        disabled={!canEdit}
                      />
                    )}
                    {(requirement.type.HasExpiration) && (!requirement.subCategory || requirement.subCategory.HasExpiration) && (
                      <FormDatePicker
                        label="Expiration Date"
                        dateFormat={DATE_FORMAT_PATTERN}
                        dateString={expirationDate}
                        setDate={setExpirationDate}
                        disabled={!canEdit}
                      />
                    )}
                    {(requirement.type.HasLocation) && (!requirement.subCategory || requirement.subCategory.HasLocation) && (
                      <div className={styles.column}>
                        <InputBox
                          label="Location Received"
                          placeholder="Provider Name & Location"
                          value={location}
                          setValue={(e) => {
                            setLocation(e.target.value);
                          }}
                          disabled={!canEdit}
                        />
                      </div>
                    )}
                  </div>
                )}
              {/* {isInfluenzaNewTemplate && (
                <div className={classNames(styles.row)}>
                  <FormDatePicker
                    label={"Date of Signature"}
                    placeholder={"Date of Signature"}
                    dateFormat={DATE_FORMAT_PATTERN}
                    dateString={dateOfSignature}
                    setDate={(value) => {
                      setDateOfSignature(value);
                      setDateOfSignatureChanged(true);
                    }}
                  />
                </div>
              )} */}
              {isInfluenzaNewTemplate && (
                <ImmunizationInfluenzaViewNew
                  data={viewResult}
                  onChange={(result) => {
                    console.log("ImmunizationInfluenzaViewNew result", result);
                    setViewResult(result);
                  }}
                  disabled={!canEdit}
                  onValidate={handleValidation}
                  ref={immunizationInfluenzaNewRef}
                />
              )}
              {requirement.type.HasLicense ? (
                <>
                  <div className={styles.innerColumnContainer}>
                    <div className={styles.innerColumn}>
                      <span className={styles.textBold}>
                        Do you hold an active healthcare license?
                      </span>
                      <QuestionToggle
                        disabled={!canEdit}
                        value={hasLicenses}
                        onClick={(value) => onSetHasLicenses(value)}
                      />
                    </div>
                  </div>
                  <div>
                    {licenses.length > 0 && (
                      <div className={styles.licenseContainer}>
                        <div className={styles.innerColumnContainer}>
                          <div className={styles.innerColumn}>
                            <span className={styles.textBold}>
                              Please enter your license information and attach
                              copies.
                              <br />
                            </span>
                          </div>
                        </div>
                      </div>
                    )}

                    {licenses.map((license, index) => {
                      return (
                        <div className={styles.licenseContainer} key={license.id}>
                          <div
                            className={classNames(styles.innerColumnContainer)}
                          >
                            <div className={classNames(styles.innerColumn)}>
                              <InputBox
                                label={"License Type"}
                                placeholder={"License Type"}
                                setValue={(e) =>
                                  handleChange(
                                    license,
                                    e.target.value,
                                    "licenseType"
                                  )
                                }
                                value={license.licenseType}
                                disabled={!canEdit}
                              />
                            </div>
                            <div className={classNames(styles.innerColumn)}>
                              <SelectBox
                                label={"State"}
                                onChange={(value) => {
                                  handleChange(license, value, "issuedBy");
                                }}
                                placeholder={"Select"}
                                options={states}
                                selected={license.issuedBy}
                                disabled={!canEdit}
                              />
                              {/* <InputBox
                            label={"Issued By"}
                            placeholder={"Issued By"}
                            setValue={(e) =>
                              handleChange(license, e.target.value, "issuedBy")
                            }
                            value={license.issuedBy}
                            disabled={!canReview}
                          /> */}
                            </div>
                            <div className={classNames(styles.innerColumn)}>
                              <InputBox
                                label={"License #"}
                                placeholder={"License #"}
                                setValue={(e) =>
                                  handleChange(
                                    license,
                                    e.target.value,
                                    "licenseNumber"
                                  )
                                }
                                value={license.licenseNumber}
                                disabled={!canEdit}
                              />
                            </div>
                            {/* <div className={classNames(styles.innerColumn)}>
                              <FormDatePicker
                                label={"Issued On"}
                                placeholder={"Issued On"}
                                setDate={(value) => {
                                  handleChange(license, value, "issuedOn");
                                }}
                                dateString={license.issuedOn}
                                dateFormat={DATE_FORMAT_PATTERN}
                                disabled={!canReview}
                              />
                            </div> */}
                            <div className={classNames(styles.innerColumn)}>
                              <FormDatePicker
                                label={"Expiration Date"}
                                placeholder={"Expiration Date"}
                                setDate={(value) => {
                                  handleChange(license, value, "expiresOn");
                                }}
                                dateString={license.expiresOn}
                                dateFormat={DATE_FORMAT_PATTERN}
                                disabled={!canEdit}
                              />
                            </div>
                          </div>
                          <div className={styles.innerColumnContainer}>
                            <div className={styles.innerColumn}>
                              <div className={styles.upload}>
                                {canEdit && (
                                  <AddButton
                                    onClick={() => onUploadLicenses(index)}
                                    label={"Add Files"}
                                    className={classNames(
                                      styles.addFileIcon,
                                      styles.actionIcons
                                    )}
                                  />
                                )}
                              </div>
                            </div>
                          </div>
                          <div
                            className={classNames(styles.innerColumnContainer)}
                          >
                            <div
                              className={classNames(
                                styles.innerColumn,
                                styles.actions
                              )}
                            >
                              {license.files && license.files.length > 0
                                ? license.files.map((f, fi) => (
                                  <DashboardPassportFileEditorItem
                                    key={fi}
                                    name={f.name}
                                    onView={() => {
                                      window.open(f.url, "_blank");
                                    }}
                                    onDelete={() => {
                                      license.files = license.files.filter(
                                        (a) => a.Id != f.Id
                                      );
                                      setLicenses([...licenses]);
                                    }}
                                    canDelete={canEdit}
                                  />
                                ))
                                : null}
                              {license.newFiles && license.newFiles.length > 0
                                ? Array.from(license.newFiles).map((f, fi) => (
                                  <DashboardPassportFileEditorItem
                                    key={fi}
                                    name={f.name}
                                    onView={() => {
                                      window.open(
                                        URL.createObjectURL(f),
                                        "_blank"
                                      );
                                    }}
                                    onDelete={() => {
                                      license.newFiles =
                                        removeFileFromFileList(
                                          license.newFiles,
                                          fi
                                        );
                                      setLicenses([...licenses]);
                                    }}
                                    canDelete={canEdit}
                                  />
                                ))
                                : null}
                            </div>
                          </div>
                          <hr />
                        </div>
                      );
                    })}

                    <input
                      type="file"
                      multiple={true}
                      onChange={(e) => onLicenseFileChange(e)}
                      ref={licenseFileRef}
                      style={{ display: "none" }}
                    />

                    {licenses.length > 0 && canEdit ? (
                      <AddButton
                        onClick={onAddMoreLicense}
                        label={"Add Another License"}
                        className={classNames(
                          styles.addFileIcon,
                          styles.actionIcons
                        )}
                      />
                    ) : null}
                  </div>
                </>
              ) : null}
            </div>
          )}

          {requirement.type.HasDeclination &&
            !userRoles.includes("STUDENT") && (
              <div className={styles.column}>
                <p className={styles.paragraph}>Declination Reason</p>
                <Button
                  className={styles.clear_button}
                  onClick={handleClearDeclination}
                  text="Clear Fields"
                />
                {isHepBNewTemplate && (
                  <div>
                    <ImmunizationHepBDeclinationForm
                      value={viewResult.reviewer}
                      onChange={(data) => {
                        // setHepBDeclination(data.declination);
                        handleDeclinationChange(data.declination);
                        setViewResult(prev => ({
                          ...prev,
                          reviewer: {
                            ...prev.reviewer,
                            declination: data.declination,
                            files: data.files || [],         // existing server files
                            newFiles: data.newFiles || [],
                            newFileNames: data.newFileNames || []
                          }
                        }));
                      }}
                      disabled={!canEdit}
                    />
                  </div>
                )}
                {!isHepBNewTemplate && (
                  <div className={styles.row}>
                    <DashboardRadio
                      isSelected={declination === DeclinationEnum.EggAllergy}
                      onChange={handleDeclinationChange}
                      value={DeclinationEnum.EggAllergy}
                      label={DeclinationEnum.EggAllergy}
                      disabled={!canEdit}
                      name="declinationRadio"
                    />
                    <DashboardRadio
                      isSelected={
                        declination === DeclinationEnum.IngredientAllergy
                      }
                      onChange={handleDeclinationChange}
                      value={DeclinationEnum.IngredientAllergy}
                      label={DeclinationEnum.IngredientAllergy}
                      disabled={!canEdit}
                      name="declinationRadio"
                    />
                    <DashboardRadio
                      isSelected={declination === DeclinationEnum.GuillainBarre}
                      onChange={handleDeclinationChange}
                      value={DeclinationEnum.GuillainBarre}
                      label={DeclinationEnum.GuillainBarre}
                      disabled={!canEdit}
                      name="declinationRadio"
                    />
                    <DashboardRadio
                      isSelected={declination === DeclinationEnum.Other}
                      onChange={handleDeclinationChange}
                      value={DeclinationEnum.Other}
                      label={DeclinationEnum.Other}
                      disabled={!canEdit}
                      name="declinationRadio"
                    />
                  </div>
                )}
              </div>

            )}
          {renderDocuments()}
          <div className={styles.row}>
            <div className={styles.column}>
              <p className={styles.paragraph}>Approval</p>
              <div className={styles.row}>
                {approvalOptions.map(m => (
                  <DashboardRadio
                    key={m.value}
                    isSelected={approval === m.value}
                    onChange={handleApprovalChange}
                    value={m.value}
                    label={m.label}
                    disabled={!canReview}
                    name="approvedRadio"
                  />
                ))}
              </div>
              <div className={styles.row}>
                {approval == "Approved" &&
                  expirationDateRequired &&
                  !isInfluenzaNewTemplate &&
                  (!requirement.subCategory || requirement.subCategory.Name != "Criminal History Disclosure") &&
                  !isBLSNewTemplate && (
                    //Expiration Date
                    <FormDatePicker
                      label={"Requirement Expiration Date"}
                      placeholder={DATE_FORMAT_PATTERN}
                      setDate={(value) => {
                        setRequirementExpirationDate(value);
                      }}
                      dateString={requirementExpirationDate}
                      dateFormat={DATE_FORMAT_PATTERN}
                      disabled={!canReview}
                    />
                  )}
                {approval == "Rejected" && (
                  <div>
                    <label htmlFor="messageContent">Why is it rejected?</label>
                    <textarea
                      name="messageContent"
                      id="messageContent"
                      ref={rejectionMessageRef}
                      className={styles.text_area}
                      onChange={(e) => setRejectionMessage(e.target.value)}
                      disabled={!canReview}
                    ></textarea>
                  </div>
                )}
                {approval === "Conditionally Approved" &&
                  !isInfluenzaNewTemplate &&
                  (!requirement.subCategory || requirement.subCategory.Name !== "Criminal History Disclosure") &&
                  !isBLSNewTemplate && expirationDateRequired && (
                    <div>
                      <div>
                        <FormDatePicker
                          label="Requirement Expiration Date"
                          placeholder={DATE_FORMAT_PATTERN}
                          setDate={(value) => setRequirementExpirationDate(value)}
                          dateString={requirementExpirationDate}
                          dateFormat={DATE_FORMAT_PATTERN}
                          disabled={!canReview}
                        />
                      </div>
                    </div>
                  )}
                {approval === "Conditionally Approved" && (
                  <div>
                    <label htmlFor="messageContent">What are the conditions?</label>
                    <textarea
                      name="messageContent"
                      id="messageContent"
                      ref={conditionalApprovalMessageRef}
                      className={styles.text_area}
                      onChange={(e) => setConditionalApprovalMessage(e.target.value)}
                      disabled={!canReview}
                    ></textarea>
                  </div>
                )}
              </div>
            </div>
          </div>
          {errors.length > 0 && (
            <ul>
              {errors.map((error, index) => {
                return error && <li key={index} className={styles.error}>{error}</li>;
              })}
            </ul>
          )}

          {(canEdit || canReview) && (
            <Button
              className={styles.custom_button}
              onClick={onSave}
              text="SAVE"
            />
          )}

          <div className={styles.error}>
            {!canReview
              ? "You don't have permissions to review this requirement!"
              : null}
          </div>
        </div>

        <div className={classNames(styles.borderRight, styles.column2)}></div>
        <div className={classNames(styles.column, styles.column3)}>
          <h3>Submission Status:</h3>
          <h3>{requirement.status}</h3>

          <hr>{/* Horizontal Rule */}</hr>
          <PassportMessageForm requirementType={requirement.createdByTypeContact} messages={messages} onSend={(text, selectedOption, repliedToMessage) => handleOnSend(text, selectedOption, repliedToMessage)}
            canMessageStudent={requirement.CanMessageStudent}
            canMessageEducation={requirement.CanMessageEducation}
            canMessageHealthcare={requirement.CanMessageHealthcare}
            canMessageReviewer={requirement.CanMessageReviewer}
          />
          <hr>{/* Horizontal Rule */}</hr>
        </div>

        {isLoading && (
          <div className={styles.loading}>
            <Loading />
          </div>
        )}
      </div>
    </>
  );
}

PassportRequirementReviewerView.propTypes = {};
